import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from '../Modal';
import MultiDeviceChart, { sameBands } from './MultiDeviceChart';
import { getPlantProfileDevices, previewPlantProfileDevices } from '../../utils/api';
import { useDevicesChartData } from '../../hooks/useDevicesChartData';
import { C, METRIC_LABELS, DEVICE_SERIES_COLORS } from '../../utils/plantConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

// The backend caps a preview request at 8 devices (each one costs a metrics
// query and a year of generated bands), which is also how many categorical
// series colors the palette defines.
const MAX_DEVICES = DEVICE_SERIES_COLORS.length;

// Time ranges the chart offers, in order. days: null draws the whole calendar
// year. 7 is the default because it is the window the health check itself
// averages, so the default view shows exactly the readings behind the verdict.
const RANGE_OPTIONS = [
	{ label: '7 days', days: 7 },
	{ label: '30 days', days: 30 },
	{ label: 'Year', days: null },
];
const DEFAULT_RANGE_DAYS = 7;
// Long enough that dragging a slider does not fire a request per frame, short
// enough that the charts feel attached to the controls.
const DEBOUNCE_MS = 350;

// Which useChartData series each previewable metric reads. All are read in
// 'daily' mode, where moist_values is the day's mean percentage — moisture has
// none of light's raw-lux vs. DLI ambiguity, so it is comparable to the bands
// either way.
const SERIES_KEY = { temperature: 'temp_values', light: 'light_values', moisture: 'moist_values' };

// A verdict is either healthy (green) or an alert the notifier would raise (red).
const statusColor = (label) => (label === 'ok' ? C.status_colors.optimal : C.status_colors.out);
const STATUS_TEXT = {
	ok: 'OK',
	bright: 'Too bright',
	shade: 'Too shady',
	hot: 'Too warm',
	cold: 'Too cold',
	wet: 'Too wet',
	dry: 'Too dry',
	unknown: 'Unknown',
};

const StatusPill = ({ label }) => (
	<span
		className="px-2 py-0.5 rounded-full text-[11px] font-semibold text-white whitespace-nowrap"
		style={{ background: statusColor(label) }}
	>
		{STATUS_TEXT[label] || label}
	</span>
);

// Format the sampled figures the verdict rests on, in the metric's own unit.
// Moisture shows two: it is graded Wet on the window's mean but Dry on the latest
// day, so the mean alone would leave a Dry pill unexplained.
const fmtSampled = (metric, p) => {
	if (metric === 'light') return `DLI avg ${Math.round(p.value_avg_7d).toLocaleString()} lux·h`;
	if (metric === 'moisture') return `avg ${Math.round(p.value_avg_7d)}% · now ${Math.round(p.value_latest)}%`;
	return `avg ${p.value_avg_7d.toFixed(1)}°C`;
};

// DeviceRow is one line of the summary strip above the chart: the colour that
// identifies the device's line, where it is, and how the edit moves its verdict.
// It doubles as the chart's table view — three of the series colours sit below
// 3:1 contrast on white, so identity must never rest on colour alone.
const DeviceRow = ({ preview, color, metric }) => {
	const changed = preview.current.status !== preview.candidate.status;

	return (
		<div className="flex items-center justify-between gap-4 flex-wrap px-3 py-2 border-b border-gray-100 last:border-b-0">
			<div className="flex items-center gap-2.5 min-w-0">
				<span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: color }} />
				<div className="min-w-0">
					<div className="font-medium text-text-strong truncate">
						{preview.name} <span className="text-text-weak font-mono text-xs">#{preview.device_id}</span>
					</div>
					<div className="text-xs text-text-weak truncate">
						{preview.room} · {preview.site}
						{preview.localized && ` · ${preview.country} overlay`}
					</div>
				</div>
			</div>
			<div className="text-right flex-shrink-0">
				{preview.sample_days === 0 ? (
					<span className="text-xs text-text-weak">No daily metrics yet</span>
				) : !preview.gradable ? (
					// Only light has a minimum-age gate; temperature and moisture are
					// graded from the first day, so they can never land here with
					// metrics present.
					<span className="text-xs text-text-weak">Not graded yet — light needs 7 days of history</span>
				) : (
					<div
						className={`flex items-center gap-2 justify-end ${changed ? 'ring-2 ring-offset-2 ring-amber-400 rounded-full px-1' : ''}`}
						title={changed ? 'This edit changes the verdict for this device' : 'Unchanged by this edit'}
					>
						<StatusPill label={preview.current.status_label} />
						<FontAwesomeIcon icon={faArrowRight} className="text-text-weak text-xs" />
						<StatusPill label={preview.candidate.status_label} />
					</div>
				)}
				<div className="text-[11px] text-text-weak font-mono mt-0.5">
					{fmtSampled(metric, preview)} · {preview.sample_days}d sampled
				</div>
			</div>
		</div>
	);
};

// DevicePreviewModal answers "what would saving this do to real plants?" for one
// metric — it grades the profile's own devices against the parameters currently
// in the editor, using the backend's real health-check code. For the general
// fallback profile those devices are the ones with no configured family.
const DevicePreviewModal = ({ isOpen, onClose, profileId, metric, params, isGeneral = false }) => {
	const [devices, setDevices] = useState([]);
	const [devicesLoading, setDevicesLoading] = useState(true);
	const [selected, setSelected] = useState(() => new Set());
	// Colour is pinned to the device, not to its position in the selection, so
	// deselecting one device never repaints the others.
	const [slots, setSlots] = useState(() => new Map());
	const [previews, setPreviews] = useState([]);
	const [previewError, setPreviewError] = useState(null);
	const [previewing, setPreviewing] = useState(false);
	const [rangeDays, setRangeDays] = useState(DEFAULT_RANGE_DAYS);

	// Load the family's devices once per open, pre-selecting a couple so the
	// chart shows something immediately.
	useEffect(() => {
		if (!isOpen) return;
		let active = true;
		setDevicesLoading(true);
		getPlantProfileDevices(profileId)
			.then((r) => (r.ok ? r.json() : { data: [] }))
			.then((j) => {
				if (!active) return;
				const list = j.data || [];
				const initial = list.slice(0, 2).map((d) => d.device_id);
				setDevices(list);
				setSelected(new Set(initial));
				setSlots(new Map(initial.map((id, i) => [id, i])));
			})
			.catch(() => active && setDevices([]))
			.finally(() => active && setDevicesLoading(false));
		return () => {
			active = false;
		};
	}, [isOpen, profileId]);

	const toggle = useCallback((deviceId) => {
		setSlots((prevSlots) => {
			const next = new Map(prevSlots);
			if (next.has(deviceId)) {
				next.delete(deviceId);
				return next;
			}
			if (next.size >= MAX_DEVICES) return prevSlots;
			// Lowest free slot, so a deselect-then-select reuses the gap rather
			// than shifting every other device's colour along.
			const taken = new Set(next.values());
			let slot = 0;
			while (taken.has(slot)) slot++;
			next.set(deviceId, slot);
			return next;
		});
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(deviceId)) next.delete(deviceId);
			else if (next.size < MAX_DEVICES) next.add(deviceId);
			return next;
		});
	}, []);

	const selectedIds = useMemo(() => [...selected], [selected]);
	const { byDevice, loading: measurementsLoading, error: measurementsError } = useDevicesChartData(selectedIds, 'daily');

	// Re-grade whenever the metric, the parameters or the selection change,
	// debounced so the chart tracks the sliders without a request per frame.
	useEffect(() => {
		if (!isOpen || !metric) return;
		if (selectedIds.length === 0) {
			setPreviews([]);
			setPreviewError(null);
			setPreviewing(false);
			return;
		}

		let active = true;
		setPreviewing(true);
		const timer = setTimeout(async () => {
			try {
				const response = await previewPlantProfileDevices(profileId, {
					metric,
					parameters: {
						phenology_class: params.phenology_class,
						hemisphere: params.hemisphere,
						base_levels: params.base_levels,
						peaks: params.peaks,
						band_scale: params.band_scale,
					},
					device_ids: selectedIds,
				});
				const json = await response.json().catch(() => ({}));
				if (!active) return;
				if (!response.ok) {
					throw new Error(json.message || json.error?.message || 'Failed to preview devices');
				}
				setPreviews(json.data || []);
				setPreviewError(null);
			} catch (err) {
				if (active) setPreviewError(err.message);
			} finally {
				if (active) setPreviewing(false);
			}
		}, DEBOUNCE_MS);

		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [isOpen, metric, profileId, params, selectedIds]);

	const colorFor = useCallback((deviceId) => DEVICE_SERIES_COLORS[(slots.get(deviceId) ?? 0) % DEVICE_SERIES_COLORS.length], [slots]);

	// Selected devices usually share one band: temperature ignores location
	// overlays entirely, and a light overlay only bites on a family that has
	// seasonal peaks (it scales their width and height, nothing else). But
	// several overlay rows do exist, so two devices in different countries can
	// legitimately differ. Verify rather than assume — when they do, drop the
	// band rather than drawing one that only applies to some of the lines.
	const bands = previews.length > 0 ? previews[0].bands : null;
	const bandsAgree = previews.every((p) => sameBands(p.bands, bands));

	const chartDevices = useMemo(
		() =>
			previews.map((p) => ({
				id: p.device_id,
				label: `${p.name} #${p.device_id}`,
				color: colorFor(p.device_id),
				// Unwindowed: the chart clips to the selected range by calendar
				// day. Slicing to the last N rows here would be wrong — a device
				// with gaps has fewer rows than days, so N rows can span months.
				points: byDevice[p.device_id]?.[SERIES_KEY[metric]] || [],
			})),
		[previews, byDevice, metric, colorFor]
	);

	// Group the picker by site → room, the way the dashboard presents devices.
	const grouped = useMemo(() => {
		const bySite = new Map();
		for (const d of devices) {
			if (!bySite.has(d.site)) bySite.set(d.site, []);
			bySite.get(d.site).push(d);
		}
		return [...bySite.entries()];
	}, [devices]);

	const metricLabel = metric ? METRIC_LABELS[metric].toLowerCase() : '';

	return (
		<Modal isOpen={isOpen} onClose={onClose} widthClass="max-w-5xl">
			<div className="mb-4">
				<h2 className="text-lg font-semibold">Preview {metricLabel} on devices</h2>
				<p className="text-sm text-text-weak">
					{isGeneral
						? `How the unsaved ${metricLabel} band grades the devices that fall back to this profile, using the same check the notifier runs. Nothing here is saved.`
						: `How the unsaved ${metricLabel} band grades this family's own devices, using the same check the notifier runs. Nothing here is saved.`}
				</p>
			</div>

			{devicesLoading ? (
				<p className="text-sm text-text-weak">Loading devices...</p>
			) : devices.length === 0 ? (
				<p className="text-sm text-text-weak">
					{isGeneral
						? 'Every device already belongs to a configured plant family, so nothing falls back to this profile right now.'
						: 'No devices are linked to this family yet. Devices link automatically when a plant photo is identified.'}
				</p>
			) : (
				<>
					<div className="flex items-start justify-between gap-4 flex-wrap mb-3">
						<div className="flex flex-col gap-2">
							{grouped.map(([site, siteDevices]) => (
								<div key={site}>
									<div className="text-[11px] font-semibold tracking-wide uppercase text-text-weak mb-1">{site}</div>
									<div className="flex flex-wrap gap-2">
										{siteDevices.map((d) => {
											const isSelected = selected.has(d.device_id);
											return (
												<label
													key={d.device_id}
													className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg text-sm cursor-pointer transition-colors ${
														isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
													}`}
												>
													<input
														type="checkbox"
														checked={isSelected}
														onChange={() => toggle(d.device_id)}
														disabled={!isSelected && selected.size >= MAX_DEVICES}
													/>
													{isSelected && <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colorFor(d.device_id) }} />}
													<span>{d.name}</span>
													<span className="text-xs text-text-weak">{d.room}</span>
												</label>
											);
										})}
									</div>
								</div>
							))}
							<p className="text-[11px] text-text-weak">Up to {MAX_DEVICES} devices at a time.</p>
						</div>

						<div className="flex gap-1 bg-gray-100 border border-gray-200 rounded-lg p-1">
							{RANGE_OPTIONS.map((r) => (
								<button
									key={r.label}
									onClick={() => setRangeDays(r.days)}
									className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
										rangeDays === r.days ? 'bg-white shadow-sm font-medium' : 'text-text-weak'
									}`}
								>
									{r.label}
								</button>
							))}
						</div>
					</div>

					{(previewError || measurementsError) && (
						<p className="text-sm text-red-600 mb-3">
							<FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" />
							{previewError || measurementsError}
						</p>
					)}

					<div className="max-h-[60vh] overflow-y-auto">
						{selected.size === 0 ? (
							<p className="text-sm text-text-weak">Pick a device to see its {metricLabel} against this profile.</p>
						) : previews.length === 0 && previewing ? (
							<p className="text-sm text-text-weak">Grading devices...</p>
						) : (
							<>
								<div className="border border-gray-200 rounded-xl mb-3 overflow-hidden">
									{previews.map((p) => (
										<DeviceRow key={p.device_id} preview={p} color={colorFor(p.device_id)} metric={metric} />
									))}
								</div>
								{!bandsAgree && (
									<p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mb-3">
										<FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" />
										These devices are graded against different bands, so no single band is drawn. Select devices from one location to see it.
									</p>
								)}
								{measurementsLoading ? (
									<div className="h-[320px] flex items-center justify-center text-sm text-text-weak">Loading measurements...</div>
								) : (
									<MultiDeviceChart metric={metric} bands={bandsAgree ? bands : null} devices={chartDevices} rangeDays={rangeDays} />
								)}
							</>
						)}
					</div>

					<p className="text-[11px] text-text-weak mt-3">
						Each device is graded against its own site's location overlay, not the Location preview selection on
						the editor. The verdict averages the last 7 days of daily metrics, as the health check does.
						{isGeneral && ' A device leaves this list as soon as its own plant family is configured.'}
					</p>
				</>
			)}
		</Modal>
	);
};

export default DevicePreviewModal;
